import React from 'react';
import PropTypes from 'prop-types';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';







const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

const colors = [
  'hsl(220, 20%, 65%)',
  'hsl(220, 20%, 42%)',
  'hsl(220, 20%, 35%)',
  'hsl(220, 20%, 25%)',
];

export default function ChartUserByCountry({ chartData, loading }) {
  const theme = useTheme();
  const evTypesData = chartData || [];
  // Check if we actually have data
  const hasData = evTypesData.length > 0;
  const total = evTypesData.reduce((sum, item) => sum + item.count, 0);



  return (
    <Card
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
    >
      <CardContent>
        <Typography component="h2" variant="subtitle1">
          EV Types Distribution
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hasData ? (
            <PieChart
              colors={colors}
              margin={{
                left: 80,
                right: 80,
                top: 80,
                bottom: 80,
              }}
              series={[
                {
                  data: evTypesData.map(item => ({
                    id: item.type,
                    value: item.count,
                    label: item.type
                  })),
                  innerRadius: 75,
                  outerRadius: 100,
                  paddingAngle: 0,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                },
              ]}
              height={260}
              width={260}
              slotProps={{
                legend: { hidden: true },
              }}
            >
              <PieCenterLabel
                primaryText={total.toLocaleString()}
                secondaryText="Total"
              />
            </PieChart>
          ) : (
            <Skeleton
              variant="circular"
              width={260}
              height={260}
              animation="wave"
              sx={{ bgcolor: theme.palette.background.paper }}
            />
          )}
        </Box>

        {hasData ? (
          evTypesData.map((type, index) => {
            const percentage = (type.count / total) * 100;
            return (
              <Stack
                key={type.type}
                direction="row"
                sx={{ alignItems: 'center', gap: 2, pb: 2 }}
              >
                <Box sx={{ width: 24, height: 24, bgcolor: colors[index % colors.length] }} />
                <Stack sx={{ gap: 1, flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: '500' }}>
                      {type.type}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {Math.round(percentage)}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      [`& .${linearProgressClasses.bar}`]: {
                        backgroundColor: colors[index % colors.length],
                      },
                    }}
                  />
                </Stack>
              </Stack>
            );
          })
        ) : (
          // Loading skeleton for country list
          [...Array(4)].map((_, index) => (
            <Stack key={index} direction="row" sx={{ alignItems: 'center', gap: 2, pb: 2 }}>
              <Skeleton variant="circular" width={24} height={24} />
              <Stack sx={{ gap: 1, flexGrow: 1 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Skeleton width={100} height={20} />
                  <Skeleton width={40} height={20} />
                </Stack>
                <Skeleton height={10} />
              </Stack>
            </Stack>
          ))
        )}
      </CardContent>
    </Card>
  );
}
